import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { setData } from 'platform/forms-system/src/js/actions';
import FormNavButtons from 'platform/forms-system/src/js/components/FormNavButtons';
import { isMinimalHeaderPath } from 'platform/forms-system/src/js/patterns/minimal-header';
import set from 'platform/utilities/data/set';
import get from 'platform/utilities/data/get';
import {
  TOGGLE_NAMES,
  useToggleLoadingValue,
  useToggleValue,
} from 'platform/utilities/feature-toggles';
import { fetchAddressSuggestions } from './fetchAddressSuggestions';
import { isKnownUndeliverableAddress } from './profileAddressMatch';
import AddressConfirmation from './AddressConfirmation';
import SuggestedAddressRadio from './SuggestedAddressRadio';
import { USER_ENTERED_VALUE, SUGGESTED_VALUE_PREFIX } from './constants';

/**
 * Returns a CustomPage that validates the address at `addressPath` against
 * the USPS-backed /v0/profile/address_validation endpoint. Most forms should
 * use `addressValidationPage()` instead, which wraps this in a complete page
 * config. See the README for usage.
 *
 * Behavior (matches the VA.gov Profile validation policy):
 * - Single confirmed high-confidence match → success alert; the normalized
 *   USPS address is applied on Continue.
 * - Otherwise, the entered address and ranked suggestions render as radio
 *   tiles with nothing pre-selected (design/a11y guidance) — Continue shows
 *   a "Select an address" error until the user chooses.
 * - No usable suggestions, or the API is down → warning alert; the entered
 *   address is kept and the user can continue. Validation never blocks.
 * - The entered address is the profile's mailing address and VA Profile has
 *   flagged it as undeliverable (mail was returned) → USPS validation is
 *   skipped, since the address is already on file and validated, and an
 *   undeliverable warning is shown instead. Behind the
 *   forms_undeliverable_address_warning toggle.
 *
 * Form data is only written when the user clicks Continue — nothing is
 * mutated on mount or radio change, so abandoning the page (or navigating
 * back) never alters or auto-saves an address the user didn't accept.
 *
 * @param {Object} config
 * @param {string} config.addressPath - Dot-notation path to the address
 *   object inside formData (e.g. "address" or "veteran.mailingAddress")
 * @param {string} [config.title="Confirm your mailing address"] - Page heading
 * @returns {React.FC} A CustomPage component
 */
export function createAddressValidationPage({
  addressPath,
  title = 'Confirm your mailing address',
}) {
  function AddressValidationCustomPage({
    goBack,
    goForward,
    contentBeforeButtons,
    contentAfterButtons,
  }) {
    const dispatch = useDispatch();
    const formData = useSelector(state => state.form?.data);
    // Read only the target address so the validation effect does not rerun on
    // unrelated form changes elsewhere in the state tree.
    const userAddressFromStore = useSelector(state =>
      get(addressPath, state.form?.data),
    );
    const profileMailingAddress = useSelector(
      state => state.user?.profile?.vapContactInfo?.mailingAddress,
    );
    const undeliverableWarningEnabled = useToggleValue(
      TOGGLE_NAMES.formsUndeliverableAddressWarning,
    );
    const featureTogglesLoading = useToggleLoadingValue();

    const [isLoading, setIsLoading] = useState(true);
    const hasResolvedValidationRef = useRef(false);
    const isMountedRef = useRef(false);
    const [userAddress, setUserAddress] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isConfirmedMatch, setIsConfirmedMatch] = useState(false);
    const [isServiceError, setIsServiceError] = useState(false);
    const [isUndeliverable, setIsUndeliverable] = useState(false);
    // No pre-selection (design/a11y guidance) — the user must actively choose.
    const [selectedValue, setSelectedValue] = useState(null);
    const [selectionError, setSelectionError] = useState(false);

    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    // Validate once per page mount and stop state updates after unmount. When
    // the profile address is flagged, wait for feature toggles so the
    // undeliverable check sees the real value; otherwise the toggle can't
    // matter, so validate right away. The decision is made once, so a later
    // change to its inputs can't restart it.
    const waitForToggles =
      featureTogglesLoading && profileMailingAddress?.badAddress === true;
    useEffect(() => {
      if (hasResolvedValidationRef.current || waitForToggles) {
        return;
      }

      hasResolvedValidationRef.current = true;

      const address = userAddressFromStore || {};
      setUserAddress(address);

      // Known to VA Profile and flagged: nothing for USPS to add, warn instead
      if (
        undeliverableWarningEnabled &&
        isKnownUndeliverableAddress(address, profileMailingAddress)
      ) {
        setIsUndeliverable(true);
        setIsLoading(false);
        return;
      }

      const validate = async () => {
        const result = await fetchAddressSuggestions(address);

        if (!isMountedRef.current) {
          return;
        }

        setSuggestions(result.suggestions);
        setIsServiceError(Boolean(result.error));

        if (result.showSuggestions && result.suggestions.length) {
          setShowSuggestions(true);
        } else if (result.suggestions.length) {
          // Single confirmed high-confidence match: the normalized USPS
          // address is applied on Continue, mirroring the Profile flow.
          setIsConfirmedMatch(true);
        }

        setIsLoading(false);
      };

      validate();
    }, [
      userAddressFromStore,
      profileMailingAddress,
      undeliverableWarningEnabled,
      waitForToggles,
    ]);

    // Maintain screen reader focus after loading resolves
    useEffect(() => {
      if (!isLoading) {
        const progressBar = document.getElementById('nav-form-header');
        if (progressBar) {
          progressBar.setAttribute('tabindex', '-1');
          progressBar.focus();
        }
      }
    }, [isLoading]);

    const onChangeSelectedAddress = event => {
      const value = event?.detail?.value;
      const isKnownSelection =
        value === USER_ENTERED_VALUE ||
        (value?.startsWith(SUGGESTED_VALUE_PREFIX) &&
          suggestions[Number(value.slice(SUGGESTED_VALUE_PREFIX.length))]);

      if (isKnownSelection) {
        setSelectedValue(value);
        setSelectionError(false);
      }
    };

    const chosenAddress = () => {
      if (isConfirmedMatch) {
        return suggestions[0]?.formAddress;
      }
      if (
        showSuggestions &&
        selectedValue?.startsWith(SUGGESTED_VALUE_PREFIX)
      ) {
        const index = Number(
          selectedValue.slice(SUGGESTED_VALUE_PREFIX.length),
        );
        return suggestions[index]?.formAddress;
      }
      return userAddress;
    };

    const handleContinue = () => {
      if (showSuggestions && !selectedValue) {
        setSelectionError(true);
        return;
      }
      const chosen = chosenAddress();
      let updated = formData;
      if (chosen && chosen !== userAddressFromStore) {
        updated = set(addressPath, chosen, formData);
        dispatch(setData(updated));
      }
      // The dispatch above lands in the store, but FormPage's connected
      // `form.data` prop is stale within this tick — pass the updated data
      // explicitly so navigation is computed from it. FormPage.onSubmit
      // destructures its argument (`({ formData }) => ...`), so it must
      // always be called with an object.
      goForward({ formData: updated });
    };

    // Minimal-header forms expect page titles at h1 (titleUI does the same).
    const headerLevel = isMinimalHeaderPath() ? 'h1' : 'h3';

    if (isLoading) {
      return (
        <va-loading-indicator
          label="Validating address"
          message="Checking your address with the U.S. Postal Service..."
          set-focus
        />
      );
    }

    return showSuggestions ? (
      <div>
        <SuggestedAddressRadio
          title={title}
          headerLevel={headerLevel}
          userAddress={userAddress}
          suggestions={suggestions}
          selectedValue={selectedValue}
          error={selectionError ? 'Select an address' : null}
          onChangeSelectedAddress={onChangeSelectedAddress}
        />
        {contentBeforeButtons}
        <FormNavButtons goBack={goBack} goForward={handleContinue} />
        {contentAfterButtons}
      </div>
    ) : (
      <div>
        <AddressConfirmation
          subHeader={title}
          headerLevel={headerLevel}
          userAddress={
            isConfirmedMatch ? suggestions[0]?.formAddress : userAddress
          }
          isConfirmedMatch={isConfirmedMatch}
          isServiceError={isServiceError}
          isUndeliverable={isUndeliverable}
        />
        {contentBeforeButtons}
        <FormNavButtons goBack={goBack} goForward={handleContinue} />
        {contentAfterButtons}
      </div>
    );
  }

  AddressValidationCustomPage.displayName = `AddressValidation(${addressPath})`;

  AddressValidationCustomPage.propTypes = {
    goBack: PropTypes.func.isRequired,
    goForward: PropTypes.func.isRequired,
    contentAfterButtons: PropTypes.node,
    contentBeforeButtons: PropTypes.node,
  };

  AddressValidationCustomPage.defaultProps = {
    contentBeforeButtons: null,
    contentAfterButtons: null,
  };

  return AddressValidationCustomPage;
}

/**
 * Returns a complete page-config entry for the address validation page, so
 * consumers don't hand-write the CustomPage/empty-schema boilerplate
 * (same ergonomics as the arrayBuilder and prefill patterns):
 *
 *   addressValidationPage: addressValidationPage({
 *     addressPath: 'address',
 *     path: 'mailing-address-validation',
 *     depends: formData => formData?.['view:formsAddressValidation'] === true,
 *   }),
 *
 * @param {Object} config
 * @param {string} config.addressPath - Dot-notation path to the address in
 *   form data (the object your addressUI page edits)
 * @param {string} [config.path="address-validation"] - Page URL path
 * @param {string} [config.title="Confirm your mailing address"] - Page heading
 * @param {Function|Object} [config.depends] - Standard page `depends` gate
 *   (e.g. a feature-toggle check); omit for an always-on page
 * @param {string} [config.pageClass] - Optional pageClass passthrough
 * @returns {Object} A forms-system page config object
 */
export function addressValidationPage({
  addressPath,
  path = 'address-validation',
  title = 'Confirm your mailing address',
  depends,
  pageClass,
}) {
  return {
    path,
    title,
    ...(depends ? { depends } : {}),
    ...(pageClass ? { pageClass } : {}),
    CustomPage: createAddressValidationPage({ addressPath, title }),
    CustomPageReview: null,
    uiSchema: {},
    schema: { type: 'object', properties: {} },
  };
}
